import { useMemo, useState } from 'react';

import { Badge, Button, Checkbox, Dialog, Input } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { soVN } from '../../../../lib/dinh-dang';
import type { IntrospectBang } from '../../types';
import { mauCot, soKhop, type Suggestion } from '../../nghiep-vu/chon-cot';

/**
 * Chép BỘ CỘT ĐANG GIỮ của một bảng sang những bảng khác, khớp theo TÊN CỘT (port `CopyCotDialog.tsx` native).
 *
 * ERP đóng gói lặp gần như nguyên si một bộ cột chứng từ (`DocEntry`, `DocNum`, `CardCode`…) qua hàng chục bảng; người
 * chọn 30 cột ở `OINV` không nên phải lặp lại ở `ODLN`, `OPCH`… — khớp tên là việc máy làm đúng hơn người. Bảng khớp 0
 * cột thì KHOÁ: áp một bộ không khớp gì là bỏ sạch mọi cột trừ khoá, và hậu quả chỉ lộ ra sau khi đã Lưu.
 */
export function CopyCotDialog({
  nguon,
  boNguon,
  bang,
  onClose,
  onApDung,
}: {
  nguon: Suggestion;
  /** Cột đang BỎ ở bảng nguồn — phần còn lại là bộ mẫu sẽ chép đi. */
  boNguon: Set<string>;
  bang: IntrospectBang[];
  onClose: () => void;
  /** Tên các bảng đích đã chọn; bên gọi tự tính `tinhBoCot()` và chọn luôn chúng để khai. */
  onApDung: (dich: string[]) => void;
}) {
  const { t, locale } = useT();
  const [tim, setTim] = useState('');
  const [chon, setChon] = useState<Set<string>>(new Set());

  const mau = useMemo(() => mauCot(nguon, boNguon), [nguon, boNguon]);

  const ungVien = useMemo(() => {
    const q = tim.trim().toLowerCase();
    return bang
      .filter((b) => b.suggestion.name !== nguon.name)
      .map((b) => ({ b, khop: soKhop(mau, b.suggestion) }))
      .filter(({ b }) => !q || b.suggestion.name.toLowerCase().includes(q))
      .sort((x, y) => y.khop - x.khop || x.b.suggestion.name.localeCompare(y.b.suggestion.name));
  }, [bang, nguon.name, mau, tim]);

  const khopDuoc = useMemo(() => ungVien.filter((x) => x.khop > 0), [ungVien]);

  const bat = (ten: string) =>
    setChon((cu) => {
      const n = new Set(cu);
      if (n.has(ten)) n.delete(ten);
      else n.add(ten);
      return n;
    });

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('troLyAiKhoCopyCot.tieuDe', { bang: nguon.name })}
      width={640}
      destroyOnHidden
      footer={
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            {t('troLyAiKhoCopyCot.huy')}
          </Button>
          <Button
            size="sm"
            disabled={chon.size === 0}
            onClick={() => {
              onApDung([...chon]);
              onClose();
            }}
          >
            {t('troLyAiKhoCopyCot.apDung', { n: soVN(chon.size, locale) })}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <p className="text-xs leading-relaxed text-content-secondary">
          {t('troLyAiKhoCopyCot.moTa', { n: soVN(mau.size, locale) })}
        </p>

        <Input
          value={tim}
          onChange={(e) => setTim(e.target.value)}
          placeholder={t('troLyAiKhoCopyCot.timPlaceholder')}
          aria-label={t('troLyAiKhoCopyCot.timPlaceholder')}
          allowClear
        />

        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-content-secondary">
          <span>
            {t('troLyAiKhoCopyCot.demUngVien', { n: soVN(khopDuoc.length, locale), m: soVN(ungVien.length, locale) })}
          </span>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              disabled={!khopDuoc.length}
              onClick={() => setChon(new Set(khopDuoc.map((x) => x.b.suggestion.name)))}
            >
              {t('troLyAiKhoCopyCot.chonHet')}
            </Button>
            <Button variant="ghost" size="sm" disabled={!chon.size} onClick={() => setChon(new Set())}>
              {t('troLyAiKhoCopyCot.boHet')}
            </Button>
          </div>
        </div>

        <div className="space-y-1">
          {ungVien.length === 0 ? (
            <p className="py-6 text-center text-sm text-content-muted">{t('troLyAiKhoCopyCot.khongCoBang')}</p>
          ) : (
            ungVien.map(({ b, khop }) => {
              const ten = b.suggestion.name;
              const tat = khop === 0;
              return (
                <label
                  key={ten}
                  className={cn(
                    'flex items-center justify-between gap-3 rounded-lg border border-default px-2.5 py-2 text-xs',
                    tat ? 'cursor-not-allowed text-content-muted' : 'cursor-pointer hover:bg-subtle',
                  )}
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <Checkbox checked={chon.has(ten)} disabled={tat} onChange={() => bat(ten)} />
                    <span className="min-w-0 truncate font-medium">{ten}</span>
                  </span>
                  <Badge variant={tat ? 'neutral' : 'primary'}>
                    {t('troLyAiKhoCopyCot.soKhop', { n: soVN(khop, locale), m: soVN(mau.size, locale) })}
                  </Badge>
                </label>
              );
            })
          )}
        </div>
      </div>
    </Dialog>
  );
}
